import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import milestoneService from '../../services/milestoneService';
import projectService from '../../services/projectService';
import './MilestoneForm.css';
import RichTextEditor from '../Common/RichTextEditor';

const MilestoneForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    project_id: '',
    name: '',
    description: '',
    due_date: '',
    completion_date: '',
    status: 'Not Started',
    order_index: 0,
    deliverables: ''
  });

  useEffect(() => {
    fetchProjects();
    if (isEdit && id) {
      fetchMilestone();
    }
  }, [isEdit, id]);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects();
      setProjects(data.projects || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchMilestone = async () => {
    try {
      setLoading(true);
      const data = await milestoneService.getMilestoneById(id);
      const milestone = data.data;
      setFormData({
        project_id: milestone.project_id || '',
        name: milestone.name || '',
        description: milestone.description || '',
        due_date: milestone.due_date ? milestone.due_date.split('T')[0] : '',
        completion_date: milestone.completion_date ? milestone.completion_date.split('T')[0] : '',
        status: milestone.status || 'Not Started',
        order_index: milestone.order_index || 0,
        deliverables: milestone.deliverables || ''
      });
      setError(null);
    } catch (err) {
      setError('Failed to fetch milestone');
      console.error('Error fetching milestone:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const milestoneData = {
        ...formData,
        order_index: parseInt(formData.order_index) || 0,
        due_date: formData.due_date || null,
        completion_date: formData.completion_date || null
      };

      if (isEdit) {
        await milestoneService.updateMilestone(id, milestoneData);
      } else {
        await milestoneService.createMilestone(milestoneData);
      }

      navigate('/app/milestones');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save milestone');
      console.error('Error saving milestone:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/app/milestones');
  };

  if (loading && isEdit) {
    return <div className="loading">Loading milestone...</div>;
  }

  return (
    <div className="milestone-form-container">
      <div className="milestone-form-header">
        <h2>{isEdit ? 'Edit Milestone' : 'Create New Milestone'}</h2>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit} className="milestone-form">
        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="name">Milestone Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              required
              placeholder="Enter milestone name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="project_id">Project *</label>
            <select
              id="project_id"
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row custom-row">
          <div className="form-group ">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="On Hold">On Hold</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="form-group ">
            <label htmlFor="due_date">Due Date</label>
            <input
              type="date"
              id="due_date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-group custom-form-grp">
            <label htmlFor="completion_date">Completion Date</label>
            <input
              type="date"
              id="completion_date"
              name="completion_date"
              value={formData.completion_date}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="mb-3">
          <div className="form-group mt-5">
              <label htmlFor="description">Description</label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                placeholder="Enter detailed project description with formatting..."
                height="300px"
              />
          </div>
          <div className="form-group mt-5">
            <label htmlFor="deliverables">Deliverables</label>
            <RichTextEditor
              value={formData.deliverables}
              onChange={(value) => setFormData(prev => ({ ...prev, deliverables: value }))}
              placeholder="Enter detailed deliverables with formatting..."
              height="300px"
            />
          </div>
        </div>
        

        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="order_index">Order Index</label>
            <input
              type="number"
              id="order_index"
              name="order_index"
              value={formData.order_index}
              onChange={handleChange}
              className="form-control"
              min="0"
              placeholder="0"
            />
            <small className="form-text text-muted">
              Used to order milestones within a project (lower numbers appear first)
            </small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Milestone' : 'Create Milestone')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MilestoneForm;


